import { useEffect, useState } from "react";
import { AlertTriangle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, newKey } from "../../lib/api";
import { SNAPSHOT, SnapshotNotice } from "../../lib/snapshot";
import { MODE_LABELS, humanize, money } from "../../lib/format";
import { CardForm, PaymentMessage } from "../payments/CardForm";
import { tierNote } from "./StepCategory";
import { useCheckoutOptions } from "../../hooks/useCheckoutOptions";
import { useSite } from "../../context/SiteContext";
import { PolicyAgreement } from "../site/LegalLinks";
import { BilledAs, CardMarks } from "../payments/CardMarks";

export const QuoteTable = ({ quote }) => (
  <table className="w-full text-left" data-testid="quote-table">
    <caption className="sr-only">Itemized amount</caption>
    <tbody>
      {quote.line_items.map((li, i) => (
        <tr key={i} className="border-b border-slate-200"><td className="py-2">{li.label}</td><td className="py-2 text-right">{money(li.amount, quote.currency)}</td></tr>
      ))}
      {quote.tier && <tr><td colSpan={2} className="py-2 text-sm text-navy" data-testid="quote-tier">{tierNote(quote.tier)}</td></tr>}
      <tr><th className="py-3 text-navy">Total due</th><td className="py-3 text-right text-lg font-bold text-navy" data-testid="quote-total">{money(quote.total, quote.currency)}</td></tr>
    </tbody>
  </table>
);

function TestPaymentPanel({ reference, token, onPaid }) {
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(false);
  const simulate = async (outcome) => {
    setBusy(outcome);
    try {
      const { data } = await api.post(`/public/registrations/${reference}/test-payment`, { token, outcome, idempotency_key: newKey() });
      if (data.payment_status === "completed") onPaid(data.registration);
      else setFailed(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-lg border-2 border-dashed border-gold bg-[#FFFBF0] p-5" data-testid="test-payment-panel">
      <p className="flex items-center gap-2 font-bold text-navy"><FlaskConical size={20} aria-hidden="true" /> SIMULATED payment (TEST)</p>
      <p className="mt-1 text-sm text-[#4A5568]">No card details are collected. Choose an outcome; the server records the simulated result. No real money is charged.</p>
      {failed && <p role="alert" className="mt-3 flex items-center gap-2 font-semibold text-burgundy" data-testid="test-payment-failed-msg"><AlertTriangle size={18} aria-hidden="true" /> Test payment failed. Payment status: failed. You can retry.</p>}
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" className="btn-navy" disabled={!!busy} onClick={() => simulate("success")} data-testid="simulate-payment-success-btn">
          {busy === "success" ? "Processing…" : "Simulate successful test payment"}</button>
        <button type="button" className="btn-outline" disabled={!!busy} onClick={() => simulate("failed")} data-testid="simulate-payment-failed-btn">
          {busy === "failed" ? "Processing…" : "Simulate failed test payment"}</button>
      </div>
    </div>
  );
}

const BLOCKED = ["pending_verification", "authorized", "processing"];

function CardPaymentPanel({ reference, token, isTest, status, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const blocked = BLOCKED.includes(last?.status || status);
  const pay = async (card, billing) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/registrations/${reference}/pay`, { token, idempotency_key: newKey(), card, billing });
      setLast(data.payment);
      if (data.payment.status === "completed") onPaid(data.registration);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="card-payment-panel">
      {blocked ? (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm font-semibold text-amber-900" data-testid="payment-verification-pending">
          {last?.message || "We are confirming your payment with the processor. Please do not pay again; we will email you once it is confirmed."}</p>
      ) : <CardForm onSubmit={pay} busy={busy} isTest={isTest} submitLabel="Pay now" testid="registration-card-form" />}
      {!blocked && <PaymentMessage payment={last} />}
    </div>
  );
}

const Review = ({ form, category }) => (
  <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2" data-testid="registration-review">
    {[["Attendee type", category?.name], ["Name", `${form.title} ${form.first_name} ${form.last_name}`], ["Email", form.email],
      ["Phone", `${form.phone_country_code} ${form.phone}`], ["Organization", form.organization], ["Job title", form.job_title],
      ["Attendance", MODE_LABELS[form.attendance_mode]], ["Dietary", form.dietary], ["Accessibility", form.accessibility], ["Special requests", form.special_requests]]
      .filter(([, v]) => v && v.trim()).map(([k, v]) => <div key={k}><dt className="font-semibold text-navy">{k}</dt><dd className="text-[#4A5568]">{v}</dd></div>)}
  </dl>
);

export default function StepPayment({ form, set, category, result, onSubmit, submitting, onPaid, onContinue, invite, verifyToken = "" }) {
  const [quote, setQuote] = useState(null);
  const { options, error: paymentError, reload: retryPayment } = useCheckoutOptions(true, result?.registration?.reference || "");
  // Never choose a payment screen from the bootstrap snapshot loaded before a mode switch.
  const payment = options?.payment || { configured: false, provider: null, is_test: null };
  const { legal, settings } = useSite();
  useEffect(() => {
    if (result || SNAPSHOT) return;
    api.post("/public/registrations/quote", { category_id: form.category_id, attendance_mode: form.attendance_mode, comp_code: form.comp_code, invite_code: form.invite_code || "", verify_token: verifyToken })
      .then((r) => setQuote(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [form.category_id, form.attendance_mode, form.comp_code, form.invite_code, result, verifyToken]);
  const q = result?.registration.quote || quote;
  const reg = result?.registration;
  const provider = options ? (reg?.is_verification ? "card_gateway" : payment.provider) : null;

  return (
    <section data-testid="step-payment" aria-labelledby="pay-h">
      <h2 id="pay-h" className="text-2xl text-navy"><span className="text-burgundy">4.</span> {invite ? "Review" : "Review & Payment"}</h2>
      {!SNAPSHOT && !options && <div role={paymentError ? "alert" : "status"} className="mt-3 rounded border p-3">
        {paymentError || "Checking current payment options…"}
        {paymentError && <button type="button" className="btn-outline ml-3" onClick={retryPayment}>Retry</button>}
      </div>}
      {invite && <p className="mt-2 font-semibold text-ciarb" data-testid="invite-complimentary-label">{invite.label}: no payment required</p>}
      <div className="mt-5 rounded-lg border border-slate-200 p-5"><Review form={form} category={category} /></div>
      <div className="mt-5 rounded-lg bg-paleblue p-5">
        <h3 className="mb-2 text-lg text-navy">Amount (calculated by server)</h3>
        {q ? <QuoteTable quote={q} /> : SNAPSHOT ? <SnapshotNotice /> : <p aria-busy="true">Calculating…</p>}
        {q?.code_provided && !q.code_valid && <p className="field-error" role="alert" data-testid="comp-code-invalid">The complimentary code is not valid. Go back to step 1 to correct or remove it.</p>}
        {q?.is_sample_price && <p className="mt-2 text-xs text-[#4A5568]">Sample prices pending confirmation.</p>}
      </div>
      {!reg && (
        <div className="mt-5">
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={form.consent} onChange={(e) => set({ consent: e.target.checked })} data-testid="reg-consent-checkbox" />
            <span>I confirm the information above is accurate and consent to CIArb Bahamas Branch processing it for this event.</span>
          </label>
          <div className="mt-3"><PolicyAgreement pages={legal?.pages} checked={form.accept_policies} onChange={(v) => set({ accept_policies: v })} testid="reg-policies-checkbox" /></div>
          <button type="button" className="btn-primary mt-5" disabled={SNAPSHOT || !options || !form.consent || !form.accept_policies || submitting || !q || (q.code_provided && !q.code_valid)} onClick={onSubmit} data-testid="submit-registration-btn">
            {submitting ? "Submitting…" : "Submit Registration"}</button>
        </div>
      )}
      {reg && reg.payment_status === "awaiting_setup" && (
        <div className="mt-5 rounded-lg border border-slate-300 p-5" data-testid="payment-setup-pending">
          <p className="font-bold text-navy">Payment setup pending</p>
          <p className="text-sm text-[#4A5568]">Online payment is not yet configured. Your registration ({reg.reference}) has been received; payment instructions will follow.</p>
          <button type="button" className="btn-navy mt-4" onClick={onContinue} data-testid="continue-to-confirmation-btn">Continue</button>
        </div>
      )}
      {reg && provider === "card_gateway" && reg.payment_status !== "completed" && (
        <div className="mt-5"><p className="mb-3 text-sm">Reference <b>{reg.reference}</b> · Payment status: <b data-testid="reg-payment-status">{humanize(reg.payment_status)}</b></p>
          <CardPaymentPanel reference={reg.reference} token={result.token} isTest={reg.is_verification ? false : payment.is_test} status={reg.payment_status} onPaid={onPaid} /></div>
      )}
      {reg && ["pending", "failed"].includes(reg.payment_status) && payment.configured && provider === "test" && (
        <div className="mt-5"><p className="mb-3 text-sm">Reference <b>{reg.reference}</b> · Payment status: <b>{humanize(reg.payment_status)}</b></p>
          <TestPaymentPanel reference={reg.reference} token={result.token} onPaid={onPaid} /></div>
      )}
      {q?.total > 0 && !invite && (
        <div className="mt-5 space-y-2 rounded-lg border border-slate-200 p-4" data-testid="reg-card-acceptance">
          <CardMarks testid="reg-card-marks" />
          <BilledAs descriptor={settings?.billing_descriptor} testid="reg-billing-descriptor" />
        </div>
      )}
    </section>
  );
}
